import i18next from 'i18next';
import $ from 'jquery';
import JSZip from 'jszip';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import notoSansRegularUrl from 'url:../../static/fonts/NotoSansTC-Regular.otf';
import notoSansBoldUrl from 'url:../../static/fonts/NotoSansTC-Bold.otf';

import DataTable from 'datatables.net-bs5';
import 'datatables.net-buttons-bs5';
import 'datatables.net-select-bs5';
import 'datatables.net-responsive-bs5';

import 'datatables.net-buttons/js/buttons.html5.mjs';
import 'datatables.net-buttons/js/buttons.print.mjs';

// DataTable CSS
import 'datatables.net-bs5/css/dataTables.bootstrap5.css';
import 'datatables.net-buttons-bs5/css/buttons.bootstrap5.css';
import 'datatables.net-responsive-bs5/css/responsive.bootstrap5.css';
import 'datatables.net-select-bs5/css/select.bootstrap5.css';

const notoSansRegular = new URL(notoSansRegularUrl, document.baseURI).href;
const notoSansBold = new URL(notoSansBoldUrl, document.baseURI).href;

pdfMake.addVirtualFileSystem(pdfFonts);
pdfMake.addFonts({
    NotoSansTC: {
        normal: notoSansRegular,
        bold: notoSansBold,
        italics: notoSansRegular,
        bolditalics: notoSansBold
    }
});

DataTable.Buttons.jszip(JSZip);
DataTable.Buttons.pdfMake(pdfMake);

const active_tables = new Map();
const text_renderer = DataTable.render.text();

function render_cell(value, type) {
    const display_value = value === null || value === undefined
        ? ''
        : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);

    if (type === 'display') {
        return text_renderer.display(display_value);
    }

    if (type === 'filter') {
        return display_value;
    }

    return value ?? '';
}

function render_dataset_link(value, type, row) {
    const display_value = String(value)

    if (type !== 'display') return display_value;

    const dataset_name = row?.name;

    const link = document.createElement('a');
    link.className = 'text-link';
    link.href = `https://data.depositar.io/dataset/${encodeURIComponent(String(dataset_name))}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = display_value;

    const icon = document.createElement('i');
    icon.className = 'bi bi-box-arrow-up-right ms-1';
    icon.setAttribute('aria-hidden', 'true');

    link.appendChild(icon);

    return link;
}

function format_percentage(value) {
    const numeric_value = Number(value) || 0;

    if (numeric_value === 0) {
        return '0%';
    }

    if (numeric_value < 0.01) {
        return '<0.01%';
    }

    const rounded_value = numeric_value
        .toFixed(2)
        .replace(/\.?0+$/, '');

    return `${rounded_value}%`;
}

function render_percentage(value, type) {
    const numeric_value = Number(value) || 0;

    const bar_width = Math.min(Math.max(numeric_value, 0), 100);

    const display_value = format_percentage(numeric_value);

    if (type === 'display') {
        return `
            <div class="dataset-view-percentage">
                <span class="dataset-view-percentage__value">
                    ${display_value}
                </span>

                <div
                    class="progress dataset-view-percentage__track"
                    aria-hidden="true"
                >
                    <div
                        class="progress-bar bg-primary"
                        style="width: ${bar_width}%"
                    ></div>
                </div>
            </div>
        `;
    }

    if (type === 'filter') {
        return display_value;
    }

    return numeric_value;
}

function get_export_buttons(export_columns) {
    const export_options = {columns: export_columns};

    return [
        {
            extend: 'copyHtml5',
            text: 'COPY <i class="bi bi-copy"></i>',
            titleAttr: 'Copy',
            exportOptions: export_options
        },
        {
            extend: 'print',
            text: 'Print <i class="bi bi-printer"></i>',
            titleAttr: 'Print',
            exportOptions: export_options
        },
        {
            extend: 'csvHtml5',
            text: 'CSV <i class="bi bi-filetype-csv"></i>',
            titleAttr: 'CSV',
            exportOptions: export_options
        },
        {
            extend: 'excelHtml5',
            text: 'Excel <i class="bi bi-filetype-xlsx"></i>',
            titleAttr: 'Excel',
            exportOptions: export_options
        },
        {
            extend: 'pdfHtml5',
            text: 'PDF <i class="bi bi-filetype-pdf"></i>',
            titleAttr: 'PDF',
            exportOptions: export_options,
            customize: function(doc) {
                doc.defaultStyle = {
                    font: 'NotoSansTC'
                };
            }
        }
    ];
}

function localize_table_headers(table_element, columns, selectable) {
    const headers = table_element.querySelectorAll('thead th');
    const offset = selectable ? 1 : 0;

    columns.forEach((column, index) => {
        const title_span = headers[index + offset]
            ?.querySelector('.dt-column-title');

        if (title_span && column.i18nKey) {
            title_span.setAttribute('data-i18n', column.i18nKey);
        }
    });

    $(table_element).localize();
}

/**
 * Modifies the export buttons of a DataTable to apply custom styles.
 *
 * @param {string} container_id - The ID of the container element containing the table.
 */
export function modify_table_export_btn(container_id) {
    const table = document.getElementById(container_id);

    const copy_btn = table.querySelector('.buttons-copy');
    copy_btn.classList.remove("btn-secondary");
    copy_btn.classList.add("btn", "btn-outline-secondary", "rounded-5", "border-2", "m-1");

    const print_btn = table.querySelector('.buttons-print');
    print_btn.classList.remove("btn-secondary");
    print_btn.classList.add("btn", "btn-outline-secondary", "rounded-5", "border-2", "m-1");

    const csv_btn = table.querySelector('.buttons-csv');
    csv_btn.classList.remove("btn-secondary");
    csv_btn.classList.add("btn", "btn-outline-primary", "rounded-5", "border-2", "m-1");

    const excel_btn = table.querySelector('.buttons-excel');
    excel_btn.classList.remove("btn-secondary");
    excel_btn.classList.add("btn", "btn-outline-success", "rounded-5", "border-2", "m-1");

    const pdf_btn = table.querySelector('.buttons-pdf');
    pdf_btn.classList.remove("btn-secondary");
    pdf_btn.classList.add("btn", "btn-outline-danger", "rounded-5", "border-2", "m-1");

    const buttons = table.querySelector('.dt-buttons');
    buttons.classList.add("mb-3");
}

function get_dataTables_lang_url(lang) {
    const map = {
      'zh': 'https://cdn.datatables.net/plug-ins/1.13.5/i18n/zh-HANT.json',
      'en': 'https://cdn.datatables.net/plug-ins/1.13.5/i18n/en-GB.json'
    };
    return map[lang];
}

export function create_DataTable({container_id, table_id, rows, columns, order = [], selectable = false, exportable = true, searchPlaceholder = false, autoWidth = true, selectColumnWidth,}) {
    const container = document.getElementById(container_id);

    if (!container) {
        throw new Error(`DataTable container not found: ${container_id}`);
    }

    const previous_table = active_tables.get(container_id);

    if (previous_table) {
        previous_table.destroy();
        active_tables.delete(container_id);
    }

    container.replaceChildren();

    const table_element = document.createElement('table');
    table_element.id = table_id;
    table_element.className = 'table table-striped table-hover';
    table_element.style.width = '100%';

    container.appendChild(table_element);

    const data_columns = columns.map(column => ({
        data: column.key,
        name: column.key,
        title: column.label,
        width: column.width,
        className: column.className || '',
        defaultContent: '',
        render: column.render || render_cell,
    }));

    if (selectable) {
        data_columns.unshift({
            data: null,
            name: 'select',
            title: '',
            width: selectColumnWidth,
            defaultContent: '',
            orderable: false,
            searchable: false,
            render: DataTable.render.select(),
        });
    }

    const layout = {
        topStart: {
            pageLength: {
                menu: [10, 25, 50, 100]
            }
        },
        topEnd: 'search'
    };

    if (exportable) {
        const export_columns = selectable
            ? ':not(:first-child)'
            : ':visible';

        layout.top2Start = {
            buttons: get_export_buttons(export_columns)
        };
    }

    const config = {
        data: rows,
        columns: data_columns,
        responsive: true,
        autoWidth,
        layout,
        order,
        language: {
            ajax: get_dataTables_lang_url(
                i18next.resolvedLanguage
            ),
            searchPlaceholder:
                i18next.resolvedLanguage === 'zh'
                    ? '請輸入關鍵字'
                    : 'Type search here'
        },
        initComplete: function() {
            localize_table_headers(
                table_element,
                columns,
                selectable
            );

            if (exportable) {
                setTimeout(() => {
                    modify_table_export_btn(container_id);
                }, 100);
            }
        }
    };

    if (selectable) {
        config.select = {
            style: 'multi',
            selector: 'td:first-child'
        };
    }

    const table = new DataTable(table_element, config);

    table.on('draw.dt', function() {
        localize_table_headers(
            table_element,
            columns,
            selectable
        );
    });

    active_tables.set(container_id, table);

    return table;
}

export function update_table_rows(table, rows = []) {
    table.clear();
    table.rows.add(rows);
    table.draw();
}

export function daily_stats_table(container_id, col_name, data, date, mapping) {
    const matched_index = data.findIndex(row => {
        return row.update_time.slice(0, 10) === date;
    });

    const source_index = matched_index >= 0
        ? matched_index
        : 0;

    const rows = JSON.parse(
        data[source_index][col_name]
    );

    if (rows.length === 0) {
        document
            .getElementById(container_id)
            ?.replaceChildren();

        return matched_index;
    }

    let keys = Object.keys(rows[0]);

    keys = keys.filter(key => {
        return key !== 'display_name' && key !== 'count';
    });

    keys.unshift('display_name');
    keys.push('count');

    const columns = keys.map(key => ({
        key,
        label: mapping[key] || key,
        i18nKey: key
    }));

    const count_column_index = keys.indexOf('count') + 1; // +1 for checkbox

    create_DataTable({container_id, table_id: `${col_name}_table`, rows, columns, selectable: true, exportable: true, order: [[count_column_index, 'desc']]});

    return matched_index;
}

export function dataset_view_by_org_table(container_id, rows, mapping) {
    const columns = [
        {
            key: 'title',
            label: mapping.title,
            i18nKey: 'tracking-tab-title',
            width: '40%',
            className: 'dataset-name-cell align-middle',
            render: render_dataset_link
        },
        {
            key: 'view_percentage',
            label: mapping.view_percentage,
            i18nKey: 'view-percentage',
            width: '45%',
            className:
                'dataset-view-percentage-cell align-middle',
            render: render_percentage
        },
        {
            key: 'tracking',
            label: mapping.tracking,
            i18nKey: 'view-tracking',
            width: '15%',
            className: 'dataset-view-count-cell text-end align-middle',
            render: function(value, type) {
                const numeric_value = Number(value) || 0;

                if (type === 'display') {
                    return numeric_value.toLocaleString();
                }

                return numeric_value;
            }
        }
    ];

    return create_DataTable({
        container_id,
        table_id: 'organization_dataset_table',
        rows,
        columns,
        selectable: true,
        selectColumnWidth: '3rem',
        exportable: true,
        order: [[3, 'desc']],
        autoWidth: false
    });
}
